import React, { useState, useEffect } from 'react';
import './StudentRoleDashboard.css';

interface RoleAssignment {
  id: string;
  sceneId: string;
  layerId: string;
  roleName: string;
  userId: string;
  isUnderstudy: boolean;
  sceneTitle?: string;
  projectId?: string;
  disciplineType?: 'music' | 'dance' | 'drama';
}

interface Project {
  id: string;
  title: string;
}

interface Scene {
  id: string;
  title: string;
  projectId: string;
}

interface RoleStatus {
  status: 'upcoming_rehearsal' | 'needs_practice' | 'ready';
  lastPracticed?: Date;
  nextRehearsal?: Date;
}

interface StudentRoleDashboardProps {
  userId: string;
}

export const StudentRoleDashboard: React.FC<StudentRoleDashboardProps> = ({ userId }) => {
  const apiBase = import.meta.env.VITE_API_URL || '/api';
  const [assignments, setAssignments] = useState<RoleAssignment[]>([]);
  const [projects, setProjects] = useState<Map<string, Project>>(new Map());
  const [scenes, setScenes] = useState<Map<string, Scene>>(new Map());
  const [roleStatuses, setRoleStatuses] = useState<Map<string, RoleStatus>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [selectedDiscipline, setSelectedDiscipline] = useState<string>('all');

  useEffect(() => {
    fetchRoleAssignments();
  }, [userId]);

  const fetchRoleAssignments = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiBase}/role-assignments/user/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch role assignments');

      const data = await response.json();
      setAssignments(data);

      // Fetch additional data for projects and scenes
      await fetchProjectsAndScenes(data);
      await fetchRoleStatuses(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectsAndScenes = async (assignments: RoleAssignment[]) => {
    const projectIds = [...new Set(assignments.map(a => a.projectId).filter(Boolean))];
    const sceneIds = [...new Set(assignments.map(a => a.sceneId))];

    // Fetch projects
    const projectsMap = new Map<string, Project>();
    for (const projectId of projectIds) {
      try {
        const response = await fetch(`${apiBase}/projects/${projectId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const project = await response.json();
          projectsMap.set(projectId as string, project);
        }
      } catch (err) {
        console.error('Error fetching project:', err);
      }
    }
    setProjects(projectsMap);

    // Fetch scenes
    const scenesMap = new Map<string, Scene>();
    for (const sceneId of sceneIds) {
      try {
        const response = await fetch(`${apiBase}/scenes/${sceneId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const scene = await response.json();
          scenesMap.set(sceneId, scene);
        }
      } catch (err) {
        console.error('Error fetching scene:', err);
      }
    }
    setScenes(scenesMap);
  };

  const fetchRoleStatuses = async (assignments: RoleAssignment[]) => {
    // Mock implementation - in real app, fetch from practice sessions and rehearsal schedules
    const statusesMap = new Map<string, RoleStatus>();
    
    for (const assignment of assignments) {
      // Determine status based on practice history and upcoming rehearsals
      const status: RoleStatus = {
        status: Math.random() > 0.5 ? 'ready' : 'needs_practice',
        lastPracticed: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000),
        nextRehearsal: Math.random() > 0.5 ? new Date(Date.now() + Math.random() * 7 * 24 * 60 * 60 * 1000) : undefined
      };
      statusesMap.set(assignment.id, status);
    }
    
    setRoleStatuses(statusesMap);
  };

  const handleNavigateToPractice = (assignment: RoleAssignment) => {
    // Navigate to practice view for this role
    window.location.href = `/practice/${assignment.layerId}?role=${encodeURIComponent(assignment.roleName)}`;
  };

  const getStatusBadge = (status: RoleStatus['status']) => {
    const badges = {
      upcoming_rehearsal: { text: 'Upcoming Rehearsal', className: 'status-upcoming' },
      needs_practice: { text: 'Needs Practice', className: 'status-needs-practice' },
      ready: { text: 'Ready', className: 'status-ready' }
    };
    return badges[status];
  };

  const getDisciplineIcon = (discipline: string) => {
    const icons = {
      music: '🎵',
      dance: '💃',
      drama: '🎭'
    };
    return icons[discipline as keyof typeof icons] || '📋';
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  };

  const groupedAssignments = assignments.reduce((acc, assignment) => {
    const projectId = assignment.projectId || 'unknown';
    if (!acc[projectId]) {
      acc[projectId] = [];
    }
    acc[projectId].push(assignment);
    return acc;
  }, {} as Record<string, RoleAssignment[]>);

  const filteredAssignments = assignments.filter(assignment => {
    if (selectedProject !== 'all' && assignment.projectId !== selectedProject) {
      return false;
    }
    if (selectedDiscipline !== 'all' && assignment.disciplineType !== selectedDiscipline) {
      return false;
    }
    return true;
  });

  const uniqueProjects = [...new Set(assignments.map(a => a.projectId).filter(Boolean))];

  if (loading) {
    return <div className="student-role-dashboard loading">Loading your roles...</div>;
  }

  if (error) {
    return <div className="student-role-dashboard error">Error: {error}</div>;
  }

  if (assignments.length === 0) {
    return (
      <div className="student-role-dashboard empty">
        <h2>My Roles</h2>
        <p>You don't have any role assignments yet.</p>
        <p>Your teacher will assign you roles in upcoming projects.</p>
      </div>
    );
  }

  return (
    <div className="student-role-dashboard">
      <h2>My Roles</h2>

      {/* Filters */}
      <div className="dashboard-filters">
        <div className="filter-group">
          <label>Project:</label>
          <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}>
            <option value="all">All Projects</option>
            {uniqueProjects.map(projectId => (
              <option key={projectId} value={projectId}>
                {projects.get(projectId as string)?.title || 'Unknown Project'}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Discipline:</label>
          <select value={selectedDiscipline} onChange={(e) => setSelectedDiscipline(e.target.value)}>
            <option value="all">All Disciplines</option>
            <option value="music">Music</option>
            <option value="dance">Dance</option>
            <option value="drama">Drama</option>
          </select>
        </div>
      </div>

      {/* Summary stats */}
      <div className="dashboard-summary">
        <div className="summary-card">
          <div className="summary-value">{assignments.length}</div>
          <div className="summary-label">Total Roles</div>
        </div>
        <div className="summary-card">
          <div className="summary-value">
            {Array.from(roleStatuses.values()).filter(s => s.status === 'ready').length}
          </div>
          <div className="summary-label">Ready</div>
        </div>
        <div className="summary-card">
          <div className="summary-value">
            {Array.from(roleStatuses.values()).filter(s => s.status === 'needs_practice').length}
          </div>
          <div className="summary-label">Needs Practice</div>
        </div>
        <div className="summary-card">
          <div className="summary-value">
            {assignments.filter(a => a.isUnderstudy).length}
          </div>
          <div className="summary-label">Understudy Roles</div>
        </div>
      </div>

      {/* Roles grouped by project */}
      <div className="roles-by-project">
        {Object.entries(groupedAssignments)
          .filter(([projectId]) => selectedProject === 'all' || projectId === selectedProject)
          .map(([projectId, projectAssignments]) => {
            const project = projects.get(projectId);
            const filteredProjectAssignments = projectAssignments.filter(assignment =>
              selectedDiscipline === 'all' || assignment.disciplineType === selectedDiscipline
            );

            if (filteredProjectAssignments.length === 0) return null;

            return (
              <div key={projectId} className="project-section">
                <h3 className="project-title">{project?.title || 'Unknown Project'}</h3>

                <div className="roles-grid">
                  {filteredProjectAssignments.map(assignment => {
                    const scene = scenes.get(assignment.sceneId);
                    const status = roleStatuses.get(assignment.id);
                    const statusBadge = status ? getStatusBadge(status.status) : null;
                    const disciplineLabel = assignment.disciplineType
                      ? assignment.disciplineType.charAt(0).toUpperCase() + assignment.disciplineType.slice(1)
                      : 'Unknown';

                    return (
                      <div key={assignment.id} className="role-card">
                        <div className="role-header">
                          <div className="role-discipline">
                            <span className="discipline-icon">
                              {getDisciplineIcon(assignment.disciplineType || '')}
                            </span>
                            <span className="discipline-name">
                              {disciplineLabel}
                            </span>
                          </div>
                          {assignment.isUnderstudy && (
                            <span className="understudy-badge">Understudy</span>
                          )}
                        </div>

                        <div className="role-content">
                          <h4 className="role-name">{assignment.roleName}</h4>
                          <p className="scene-name">{scene?.title || 'Unknown Scene'}</p>

                          {statusBadge && (
                            <div className={`status-badge ${statusBadge.className}`}>
                              {statusBadge.text}
                            </div>
                          )}

                          {status && (
                            <div className="role-details">
                              {status.lastPracticed && (
                                <div className="detail-item">
                                  <span className="detail-label">Last practiced:</span>
                                  <span className="detail-value">{formatDate(status.lastPracticed)}</span>
                                </div>
                              )}
                              {status.nextRehearsal && (
                                <div className="detail-item">
                                  <span className="detail-label">Next rehearsal:</span>
                                  <span className="detail-value">{formatDate(status.nextRehearsal)}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="role-actions">
                          <button
                            onClick={() => handleNavigateToPractice(assignment)}
                            className="practice-button"
                          >
                            Practice This Role
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default StudentRoleDashboard;
